import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { authAPI } from "../services/api";
import { identifySentryUser } from "../lib/sentry";
import { registerPushIfSupported } from "../services/pushNotifications";
import { resetPermissionsCache } from "../hooks/usePermissions";

const AuthContext = createContext(null);

// Turn a FastAPI error `detail` into a display string. FastAPI 422 returns an
// ARRAY of {msg,...} objects (not a string); rendering that raw crashes React,
// so normalise every shape here.
function formatApiErrorDetail(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// True only when the server actually answered with a definitive auth
// rejection. A missing `response` means the request never got a real answer
// (offline, VPN/proxy drop, DNS, CORS, gateway 5xx) — that must NOT end a
// valid session.
function isDefiniteAuthRejection(err) {
  const status = err?.response?.status;
  return !!err?.response && (status === 401 || status === 403);
}

// HIPAA automatic logoff (§164.312(a)(2)(iii)): end the session after a
// short period of inactivity. 15 min total = 13 min idle → 2 min warning
// countdown → logout. Matches the policy shown on Security Settings.
const SESSION_TIMEOUT_MS = 15 * 60 * 1000;        // 15 minutes of inactivity → logout
const WARNING_BEFORE_MS  = 2 * 60 * 1000;         // Warning 2 minutes before expiry
const WARNING_AT_MS = SESSION_TIMEOUT_MS - WARNING_BEFORE_MS;
const REFRESH_INTERVAL_MS = 10 * 60 * 1000;       // Silent token refresh every 10 min while active

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [timeoutCountdown, setTimeoutCountdown] = useState(120);

  // Tag Sentry events with the current user so backend + frontend
  // crashes can be correlated to a real person. Clears when they
  // log out. No-ops in environments where Sentry isn't configured.
  useEffect(() => {
    identifySentryUser(user ? { id: user.id, email: user.email, role: user.role } : null);
    // Auto-refresh Web Push subscription for signed-in office staff.
    // First-time visits fall through with permission="default"; the
    // header's bell prompt will pick them up when they click Enable.
    // PCWs are excluded — they use the mobile app for pings.
    if (user && user.role !== "pcw") {
      registerPushIfSupported().catch(() => {});
    }
  }, [user]);

  // Mirror the authenticated profile into localStorage so the many UI
  // role-gates that read localStorage.getItem("user") resolve correctly.
  // The credential (token) stays in the httpOnly cookie — only the
  // non-secret profile is cached here, and it's cleared on logout.
  useEffect(() => {
    try {
      if (user) localStorage.setItem("user", JSON.stringify(user));
      else localStorage.removeItem("user");
    } catch { /* storage unavailable (private mode) — useAuth() still works */ }
  }, [user]);

  const lastActivityRef = useRef(Date.now());
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownRef = useRef(null);

  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    warningTimerRef.current = null;
    logoutTimerRef.current = null;
    countdownRef.current = null;
  }, []);

  const performLogout = useCallback(() => {
    // HIPAA auto-logoff must be a COMPLETE logout: hit the server to clear the
    // httpOnly cookie AND rotate the session id (kills the JWT) BEFORE we drop
    // local state and redirect. Without this the cookie survived the timeout
    // and the user stayed authenticated server-side ("not logged off"). Both
    // calls ride the cookie (credentials:"include") with keepalive so they
    // survive the imminent navigation.
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      fetch(`${base}/api/audit/logout-event`, {
        method: "POST", keepalive: true, credentials: "include",
        headers: { "Content-Type": "application/json" }, body: "{}",
      }).catch(() => {});
      fetch(`${base}/api/auth/logout`, {
        method: "POST", keepalive: true, credentials: "include",
      }).catch(() => {});
    } catch (_) { /* swallow */ }
    clearAllTimers();
    setShowTimeoutWarning(false);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    resetPermissionsCache();
    setUser(null);
    window.location.href = "/login?reason=timeout";
  }, [clearAllTimers]);

  // Mark the user as active NOW. The monitor loop below is the SINGLE source
  // of truth for the idle countdown — it just reads this timestamp, so
  // re-renders, effect churn, or background events can no longer accidentally
  // cancel a pending logout.
  const resetSessionTimers = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowTimeoutWarning(false);
  }, []);

  const extendSession = useCallback(async () => {
    try {
      await authAPI.refreshToken();
      setShowTimeoutWarning(false);
      resetSessionTimers();
    } catch (err) {
      // Only sign out if the server DEFINITIVELY rejected us. A transient
      // network blip must not end a valid session — dismiss the warning and
      // keep the user working; timers re-arm on their next activity.
      if (isDefiniteAuthRejection(err)) {
        performLogout();
      } else {
        setShowTimeoutWarning(false);
        resetSessionTimers();
      }
    }
  }, [resetSessionTimers, performLogout]);

  // Track GENUINE user activity. We deliberately (a) require e.isTrusted so
  // synthetic/programmatic events are ignored, and (b) EXCLUDE the `scroll`
  // event — programmatic auto-scroll (chat jumping to the newest message,
  // live-updating panels) fires real `scroll` events that were silently
  // re-stamping activity so users NEVER auto-logged-off. `wheel` / `touchstart`
  // / `mousedown` still capture real scrolling intent.
  useEffect(() => {
    if (!user) return;

    const handleActivity = (e) => {
      if (e && e.isTrusted === false) return;
      // Don't let activity silently dismiss the warning — the user must click
      // "Stay signed in". They can still act after we hide it via extendSession.
      if (showTimeoutWarning) return;
      const now = Date.now();
      if (now - lastActivityRef.current > 1000) {
        lastActivityRef.current = now;
      }
    };

    const events = ["mousemove", "mousedown", "keydown", "click", "touchstart", "wheel"];
    events.forEach(e => document.addEventListener(e, handleActivity, { passive: true }));

    return () => {
      events.forEach(e => document.removeEventListener(e, handleActivity));
    };
  }, [user, showTimeoutWarning]);

  // AUTHORITATIVE idle monitor — the only thing that logs an idle user out.
  // One steady 1s interval compares now vs. the last real-activity timestamp
  // and drives the 2-minute warning and the hard logout. It owns no per-cycle
  // timers, so render/effect churn can't defeat it (the previous setTimeout
  // chain could be cleared/rescheduled and would never complete).
  useEffect(() => {
    if (!user) return;
    const id = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current;
      if (idle >= SESSION_TIMEOUT_MS) {
        performLogout();
      } else if (idle >= WARNING_AT_MS) {
        setShowTimeoutWarning(true);
        setTimeoutCountdown(Math.max(0, Math.ceil((SESSION_TIMEOUT_MS - idle) / 1000)));
      }
    }, 1000);
    return () => clearInterval(id);
  }, [user, performLogout]);

  // Silent background token refresh: every 30 min while the user is
  // logged in, request a fresh JWT so they don't get kicked out mid-task
  // when the absolute server-side token expiry hits.
  useEffect(() => {
    if (!user) return;
    const tick = async () => {
      try {
        await authAPI.refreshToken();
      } catch {
        // Silent — the response interceptor will handle a real auth failure
      }
    };
    const id = setInterval(tick, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [user]);

  // On login, stamp activity so the idle monitor starts a fresh countdown.
  useEffect(() => {
    if (user) {
      lastActivityRef.current = Date.now();
    } else {
      clearAllTimers();
    }
  }, [user, clearAllTimers]);

  const loadUser = useCallback(async () => {
    // Session rides in the httpOnly cookie now (DEF-23) — JS can't read it,
    // so always ask the server who we are. A 401 (no valid cookie) just means
    // "not logged in" and is handled in the catch below.
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
      // Best-effort silent push subscription — once per app session.
      // Browser caches the SW so duplicate calls are cheap, but a
      // module-level flag avoids the round-trip on every getMe().
      if (!window.__avidaPushRegistered) {
        window.__avidaPushRegistered = true;
        import("../services/pushNotifications")
          .then((m) => m.registerPushIfSupported())
          .catch(() => { /* push is optional */ });
      }
    } catch (err) {
      console.error("Failed to load user:", err);
      // Only drop the stored session on a definitive auth rejection. On a
      // transient network error at startup (offline/VPN blip, gateway 5xx),
      // the session cookie is still valid, so a later retry (refresh-user
      // event, silent refresh, or reload) re-hydrates via /auth/me. The
      // profile is NEVER cached in localStorage (DEF-24) — it lives only in
      // React state while the app is open.
      if (isDefiniteAuthRejection(err)) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        resetPermissionsCache();
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Listen for refresh-user events
  useEffect(() => {
    const handler = () => loadUser();
    window.addEventListener("refresh-user", handler);
    return () => window.removeEventListener("refresh-user", handler);
  }, [loadUser]);

  // PROACTIVE session-expiry detection. The axios interceptor only catches a
  // dead session on requests that go through the shared `api` instance — but
  // several screens call the backend with raw fetch(), so when the session
  // dies those requests fail silently and the user is left staring at a frozen
  // / blank screen with no hint they've been logged off (they only find out
  // when a manual reload bounces them to /login). To fix that, re-validate the
  // session via /auth/me whenever the tab regains focus/visibility and on a
  // slow heartbeat. getMe() rides the interceptor, so a truly-dead session
  // triggers the standard silent-refresh-then-redirect (with a clear toast),
  // while a still-valid session is a cheap no-op. Debounced so we never spam.
  useEffect(() => {
    if (!user) return;
    let lastCheck = Date.now();
    const DEBOUNCE_MS = 20 * 1000;
    const HEARTBEAT_MS = 60 * 1000;

    const revalidate = () => {
      if (document.visibilityState === "hidden") return;
      const now = Date.now();
      if (now - lastCheck < DEBOUNCE_MS) return;
      lastCheck = now;
      authAPI.getMe().catch(() => { /* interceptor handles a dead session */ });
    };
    const onVisible = () => { if (document.visibilityState === "visible") revalidate(); };

    window.addEventListener("focus", revalidate);
    document.addEventListener("visibilitychange", onVisible);
    const hb = setInterval(revalidate, HEARTBEAT_MS);
    return () => {
      window.removeEventListener("focus", revalidate);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(hb);
    };
  }, [user]);

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await authAPI.login({ email, password });
      const data = response.data;

      // Check if 2FA is required
      if (data.requires_2fa) {
        return { requires_2fa: true, temp_token: data.temp_token };
      }

      // HIPAA MFA enforcement: grace expired → user must enroll before a
      // full session is issued. No token is stored here.
      if (data.requires_mfa_enrollment) {
        return { requires_mfa_enrollment: true, temp_token: data.temp_token };
      }

      const { user: userData } = data;
      setUser(userData);
      // Hydrate the full profile (incl. bylyngo_access and other flags the login
      // payload may omit) so gated nav/data resolve correctly without a reload.
      authAPI.getMe().then((me) => { if (me?.data) setUser(me.data); }).catch(() => {});
      return {
        user: userData,
        // grace nag (still enrolled-optional within the window)
        mfa_enrollment_required: data.mfa_enrollment_required || false,
        mfa_grace_days_remaining: data.mfa_grace_days_remaining,
      };
    } catch (err) {
      let message;
      if (!err.response) {
        // No response at all → the request never reached the server or got no
        // real answer (offline, VPN/proxy drop, DNS, CORS, gateway 5xx). This
        // is a CONNECTION problem, not a wrong password — say so, otherwise the
        // user retries valid credentials and can't tell what's wrong.
        message = "Can't reach the server. Check your internet connection and try again.";
      } else if (err.response.status === 429) {
        // Iter 475 — slowapi's default 429 has no `detail` field.
        const raw = err.response.data;
        message = (typeof raw === "string" && raw)
          ? raw
          : (formatApiErrorDetail(raw?.detail) || "Too many login attempts. Please wait a minute and try again.");
      } else {
        message = formatApiErrorDetail(err.response?.data?.detail) || "Login failed";
      }
      setError(message);
      throw new Error(message);
    }
  };

  const pcwLogin = async (email, password) => {
    setError(null);
    try {
      const response = await authAPI.pcwLogin({ email, password });
      const data = response.data;
      if (data.requires_2fa) {
        return { requires_2fa: true, temp_token: data.temp_token };
      }
      const { user: userData } = data;
      setUser(userData);
      return { user: userData };
    } catch (err) {
      let message;
      if (!err.response) {
        message = "Can't reach the server. Check your internet connection and try again.";
      } else if (err.response.status === 429) {
        const raw = err.response.data;
        message = (typeof raw === "string" && raw)
          ? raw
          : (formatApiErrorDetail(raw?.detail) || "Too many login attempts. Please wait a minute and try again.");
      } else {
        message = formatApiErrorDetail(err.response?.data?.detail) || "Login failed";
      }
      setError(message);
      throw new Error(message);
    }
  };

  const verify2FALogin = async (tempToken, code) => {
    setError(null);
    try {
      const response = await authAPI.verify2FALogin(tempToken, code);
      const { user: userData } = response.data;
      setUser(userData);
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || "2FA verification failed";
      setError(message);
      throw new Error(message);
    }
  };

  const verify2FARecovery = async (tempToken, code) => {
    setError(null);
    try {
      const response = await authAPI.verify2FARecovery(tempToken, code);
      const { user: userData } = response.data;
      setUser(userData);
      return { user: userData, recovery_codes_remaining: response.data.recovery_codes_remaining };
    } catch (err) {
      const message = err.response?.data?.detail || "Recovery code verification failed";
      setError(message);
      throw new Error(message);
    }
  };

  const register = async (userData) => {
    setError(null);
    try {
      const response = await authAPI.register(userData);
      const { user: newUser } = response.data;
      setUser(newUser);
      return newUser;
    } catch (err) {
      const message = err.response?.data?.detail || "Registration failed";
      setError(message);
      throw new Error(message);
    }
  };

  const logout = () => {
    // Clear the httpOnly session cookie server-side and emit the sign-out
    // audit event. Both ride on the cookie (credentials:"include"), so no JWT
    // is read from JS. keepalive lets them survive the page unload.
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      fetch(`${base}/api/audit/logout-event`, {
        method: "POST", keepalive: true, credentials: "include",
        headers: { "Content-Type": "application/json" }, body: "{}",
      }).catch(() => {});
      fetch(`${base}/api/auth/logout`, {
        method: "POST", keepalive: true, credentials: "include",
      }).catch(() => {});
    } catch (_) { /* swallow */ }
    clearAllTimers();
    setShowTimeoutWarning(false);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    resetPermissionsCache();
    setUser(null);
  };

  const updateStatus = async (status) => {
    try {
      const response = await authAPI.updateStatus(status);
      setUser(response.data);
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  // Persist a session obtained outside the normal login() flow — used by the
  // forced MFA-enrollment step, which returns a full token from verify-setup.
  const applyAuth = (access_token, userData) => {
    // Neither the token nor the profile is persisted to localStorage — the
    // session rides in the httpOnly cookie (DEF-23) and the profile lives in
    // React state only (DEF-24). A reload re-hydrates via /auth/me.
    setUser(userData);
  };

  const value = {
    user,
    loading,
    error,
    login,
    pcwLogin,
    verify2FALogin,
    verify2FARecovery,
    register,
    logout,
    updateStatus,
    applyAuth,
    isAuthenticated: !!user,
    showTimeoutWarning,
    timeoutCountdown,
    extendSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
